<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref } from "vue"
import { useI18n } from "vue-i18n"
import type { Services } from "@services"
import { useAuthStore } from "@core/stores/auth.store"
import { formatIdSource } from "@core/helpers"

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    /** Identifiant réel du compteur (PCE) pour le contexte support */
    idsource: string
}>()

const emit = defineEmits<{
    back: []
}>()

// ================================
// SERVICES
// ================================

const services = inject<Services>("services")

// ================================
// STORES
// ================================

const authStore = useAuthStore()

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title_alert: "Alerte",
            heading:
                "La Déclaration de Droit d'Accès (DDA) est en cours de validation",
            helper_mail:
                "La demande de droit d'accès est en attente de validation (e-mail GRDF).",
            contact_support: "Contacter le support",
            return: "Retour",
            subject_issue: "Erreur ajout PCE, compteur à valider",
            support_body:
                "Demande d'assistance — PCE {idsource} — DDA en attente de validation côté GRDF.",
            support_success:
                "Message envoyé ! Nous vous recontactons dans les plus brefs délais.",
            support_failure:
                "Une erreur est survenue lors de l'envoi du message, veuillez réessayer.",
        },
    },
})

// ================================
// STATE
// ================================

/** Message retour après appel support (succès ou erreur) */
const feedbackMessage = ref("")
/** Indique si le dernier envoi support a échoué */
const isFeedbackError = ref(false)
/** Chargement du bouton « Contacter le support » */
const isSupportLoading = ref(false)

// ================================
// COMPUTED
// ================================

/** PCE formaté pour les textes */
const getFormattedPce = computed(() => formatIdSource(props.idsource, "pce"))

// ================================
// INTERACTIONS
// ================================

function onBack(): void {
    emit("back")
}

/**
 * Envoie une demande au support (équivalent legacy $sendSupportIssue), sans machine d'état.
 */
async function onContactSupport(): Promise<void> {
    feedbackMessage.value = ""
    isFeedbackError.value = false

    if (!services?.$reportBugIssue) {
        feedbackMessage.value = t("support_failure")
        isFeedbackError.value = true
        return
    }

    const currentPage =
        typeof window !== "undefined" ? window.location.href : ""

    isSupportLoading.value = true
    try {
        await services.$reportBugIssue({
            subject: t("subject_issue"),
            description: {
                issueSubject: t("subject_issue"),
                text: t("support_body", {
                    idsource: getFormattedPce.value || props.idsource || "—",
                }),
                currentPage,
                username: authStore.username ?? "",
                idsource: props.idsource,
            },
            tags: { context: "erreur d'ajout" },
        })
        feedbackMessage.value = t("support_success")
        isFeedbackError.value = false
    } catch {
        feedbackMessage.value = t("support_failure")
        isFeedbackError.value = true
    } finally {
        isSupportLoading.value = false
    }
}
</script>

<template>
    <!-- ================================ -->
    <!-- Attente validation DDA GRDF -->
    <!-- ================================ -->
    <div class="pce-form-waiting-dda flex flex-col gap-4">
        <BaseMessage
            severity="warn"
            :pt="{
                content: 'flex flex-col justify-center',
                text: 'flex flex-col gap-6',
            }"
        >
            <div class="flex items-center justify-center gap-2">
                <i class="pi pi-info-circle text-xl" aria-hidden="true" />
                <h3 class="text-lg font-semibold">
                    {{ t("title_alert") }}
                </h3>
            </div>
            <p class="text-center text-base">
                {{ t("heading") }}
            </p>
            <p class="text-center text-gray-600 text-sm">
                {{ t("helper_mail") }}
            </p>

            <div class="flex flex-col gap-4">
                <BaseMessage
                    v-if="feedbackMessage"
                    :severity="isFeedbackError ? 'error' : 'success'"
                    class="w-full"
                >
                    {{ feedbackMessage }}
                </BaseMessage>
            </div>

            <BaseButton
                :label="t('contact_support')"
                :loading="isSupportLoading"
                :disabled="isSupportLoading"
                class="w-fit self-center"
                @click="onContactSupport"
            />
        </BaseMessage>

        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
        >
            <BaseButton
                :label="t('return')"
                severity="secondary"
                class="min-w-[10rem] w-full md:w-auto self-start"
                :disabled="isSupportLoading"
                @click="onBack"
            />
        </div>
    </div>
</template>
